import React, {useCallback, useState} from 'react';
import {useDispatch} from 'react-redux';

import {BulkActionProgress} from 'src/components/bulk-tool';
import {STACKOVERFLOW_MESSAGE} from 'src/constants';
import {useBackgroundTaskManager} from 'src/contexts/background-task-context';
import {TOOL_PROGRESS_LABEL, TOOL_TASK} from 'src/modules/bulk-update/update-admin-role';
import {
    selectUpdateAdminRoleTask,
    selectUpdateAdminRoleUuid,
    setBackgroundTask,
    setShown,
    setUuid,
} from 'src/modules/bulk-update/update-admin-role/update-admin-role.slice';
import {
    bulkUpdateAdminRole,
    getAdminRoleUpdateProgress,
    stopAdminRoleUpdate,
} from 'src/openapi/admin/apis/BulkUpdateAdminRoleApi';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';

import type {ActionOptions} from 'src/components/save-with-reason/action-with-reason-button';
import type {AdminSearchInfo} from 'src/openapi/admin/models/AdminSearchInfo';

type Props = {
    onDone: () => void,
    roleId: number,
};

export const useAssignUser = ({roleId, onDone}: Props) => {
    const dispatch = useDispatch();
    const apiDispatch = useAppDispatch();

    const [assignedUsers, setAssignedUsers] = useState<Array<AdminSearchInfo>>([]);
    const createBackgroundTask = useBackgroundTaskManager();

    const assignUser = useCallback((user: AdminSearchInfo) => {
        setAssignedUsers(users => [...users, user]);
    }, []);

    const removeUser = useCallback((index: number) => {
        setAssignedUsers(users => users.filter((user, i) => i !== index));
    }, []);

    const assignUsers = useCallback((addedUsers: Array<AdminSearchInfo>) => {
        setAssignedUsers(users => [...users, ...addedUsers]);
    }, []);

    const saveUsers = useCallback(async (options?: ActionOptions) => {
        const response = await apiDispatch(bulkUpdateAdminRole({
            ...options,
            bulkUpdateAdminRoleRequest: {
                usersIds: assignedUsers.map(({userInfo}) => userInfo.userId),
                newRoleId: roleId,
            },
        }));

        if (response.payload.success) {
            const updateUsersTask = createBackgroundTask(
                TOOL_TASK,
                <BulkActionProgress
                    label={TOOL_PROGRESS_LABEL}
                    taskSelector={selectUpdateAdminRoleTask}
                    idSelector={selectUpdateAdminRoleUuid}
                    getProgress={id => apiDispatch(getAdminRoleUpdateProgress({id}))}
                    stopAction={id => apiDispatch(stopAdminRoleUpdate({id}))}
                    setShown={setShown}
                    onDone={onDone}
                />,
                {
                    maxStack: 1,
                    stackOverflowMessage: STACKOVERFLOW_MESSAGE,
                },
            );

            if (updateUsersTask) {
                dispatch(setUuid(response.payload.data));
                dispatch(setBackgroundTask(updateUsersTask));
            }
        } else {
            return getError({error: response.payload.data, separator: ' '});
        }
    }, [assignedUsers, roleId, onDone, apiDispatch, createBackgroundTask, dispatch]);

    return {
        assignedUsers,
        assignUser,
        assignUsers,
        removeUser,
        saveUsers,
    };
};
